import React from 'react';
import { Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;
const IS_WIDE_LAYOUT = WIDTH >= 1080;

const contactGroups = [
    {
        icon: 'storefront-outline',
        title: 'Camp owners',
        description: 'For camp operators, administrators, and teams managing listings on SnapCamp.',
        items: [
            {
                label: 'Onboarding inquiries',
                details: 'Questions about joining the platform, setup, approvals, and getting your first camp live.',
                email: 'campsupport+onboarding@snapcamp.ai',
            },
            {
                label: 'Camp profile management',
                details: 'Updates to listings, session details, policies, photos, availability, and account management.',
                email: 'campsupport+profiles@snapcamp.ai',
            },
            {
                label: 'Payment questions',
                details: 'Payout timing, billing issues, payment setup, and transaction-related support.',
                email: 'campsupport+payments@snapcamp.ai',
            },
            {
                label: 'Bug reports',
                details: 'Technical issues, broken flows, display bugs, and problems using the platform.',
                email: 'campsupport+bugs@snapcamp.ai',
            },
        ],
    },
    {
        icon: 'account-heart-outline',
        title: 'Parents and families',
        description: 'For parents who need help using the marketplace or resolving an issue.',
        items: [
            {
                label: 'Parent support',
                details: 'Questions about browsing camps, accounts, registration flows, and general platform use.',
                email: 'parentsupport@snapcamp.ai',
            },
            {
                label: 'Complaints and safety concerns',
                details: 'Report serious issues, submit complaints, or escalate concerns that need review.',
                email: 'parentsupport+complaints@snapcamp.ai',
            },
        ],
    },
    {
        icon: 'briefcase-outline',
        title: 'Facilitators, partners, and investors',
        description: 'For people and organizations interested in working with SnapCamp beyond camp enrollment.',
        items: [
            {
                label: 'Join as a facilitator or partner',
                details: 'Reach out about joining the platform, running programs, or partnering with SnapCamp.',
                email: 'partnerships@snapcamp.ai',
            },
            {
                label: 'Investment inquiries',
                details: 'For strategic conversations, investor outreach, and business development discussions.',
                email: 'invest@snapcamp.ai',
            },
        ],
    },
];

const ContactUsScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <Text style={styles.eyebrow}>Contact Us</Text>
                    <View style={styles.heroContent}>
                        <View style={styles.heroTitleWrap}>
                            <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Contact the right SnapCamp team for your question.</h1>
                        </View>
                        <View style={styles.heroBodyWrap}>
                            <Text style={styles.heroSubtitle}>
                                If you need help and cannot find what you need in the Help Center, use the sections below to choose the inbox that best matches your inquiry.
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Email directories by inquiry type</Text>
                    <Text style={styles.sectionSubtitle}>
                        Use the appropriate contact line below so your request reaches the right team as quickly as possible.
                    </Text>

                    <View style={styles.groupList}>
                        {contactGroups.map((group) => (
                            <View key={group.title} style={styles.groupCard}>
                                <View style={styles.groupHeader}>
                                    <View style={styles.groupIconWrap}>
                                        <MaterialCommunityIcons name={group.icon as any} size={22} color="#1D4ED8" />
                                    </View>
                                    <View style={styles.groupHeaderCopy}>
                                        <Text style={styles.groupTitle}>{group.title}</Text>
                                        <Text style={styles.groupDescription}>{group.description}</Text>
                                    </View>
                                </View>

                                <View style={styles.contactList}>
                                    {group.items.map((item) => (
                                        <View key={item.label} style={styles.contactRow}>
                                            <View style={styles.contactCopy}>
                                                <Text style={styles.contactLabel}>{item.label}</Text>
                                                <Text style={styles.contactDetails}>{item.details}</Text>
                                            </View>
                                            <View style={styles.emailPill}>
                                                <MaterialCommunityIcons name="email-outline" size={16} color="#1D4ED8" />
                                                <Text style={styles.emailText}>{item.email}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.footerNote}>
                    <MaterialCommunityIcons name="information-outline" size={18} color="#64748B" />
                    <Text style={styles.footerNoteText}>
                        Include your camp name, booking details, or account email in your message so the right team can assist you faster.
                    </Text>
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
    },
    hero: {
        paddingBottom: 32,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        marginBottom: 32,
        width: '100%',
    },
    heroContent: {
        flexDirection: IS_WIDE_LAYOUT ? 'row' : 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 28,
    },
    heroTitleWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 560 : undefined,
    },
    heroBodyWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 500 : undefined,
    },
    eyebrow: {
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#1D4ED8',
        marginBottom: 12,
    },
    heroTitle: {
        fontSize: 38,
        lineHeight: 46,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 12,
        maxWidth: 900,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
        maxWidth: 880,
    },
    section: {
        marginBottom: 40,
        width: '100%',
    },
    sectionTitle: {
        fontSize: 30,
        lineHeight: 38,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    sectionSubtitle: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 20,
        maxWidth: 900,
    },
    groupList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'space-between',
        alignItems: 'stretch',
    },
    groupCard: {
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 24,
        padding: 28,
        backgroundColor: '#FFFFFF',
        width: IS_WIDE_LAYOUT ? '48.8%' : '100%',
    },
    groupHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 22,
    },
    groupIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 16,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
    },
    groupHeaderCopy: {
        flex: 1,
    },
    groupTitle: {
        fontSize: 24,
        lineHeight: 30,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 6,
    },
    groupDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    contactList: {
        gap: 14,
    },
    contactRow: {
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
        paddingTop: 16,
    },
    contactCopy: {
        marginBottom: 12,
    },
    contactLabel: {
        fontSize: 17,
        lineHeight: 24,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 4,
    },
    contactDetails: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    emailPill: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#BFDBFE',
        backgroundColor: '#F8FBFF',
    },
    emailText: {
        fontSize: 14,
        lineHeight: 20,
        color: '#1D4ED8',
        fontWeight: '700',
    },
    footerNote: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        paddingTop: 4,
        width: '100%',
    },
    footerNoteText: {
        flex: 1,
        fontSize: 14,
        lineHeight: 22,
        color: '#64748B',
    },
});

export default ContactUsScreen;
